import React from 'react';
import { allFormants, diphthongFormants, getFormants, nonsyllabicFormants, rhoticFormants, syllabicFormants } from '../../phonemes/formants';
import { IpaType } from '../../phonemes/ipa';

interface FormantsProps {
  readonly width: number;
  readonly height: number;
  /** draw lines representing diphthongs */
  readonly diphthongs?: IpaType[] | Record<IpaType, number[]>;
  /** what to display for a given IPA string (default: ipa) */
  readonly lookup?: (ipa: IpaType) => string;
  /** what color to use for a given IPA string (default: black) */
  readonly whatColor?: (ipa: IpaType) => string;
  /** called when a symbol is clicked on */
  readonly clicked?: (ipa: IpaType) => void;
  readonly rightClicked?: (ipa: IpaType) => void;
  /** called when a diphthong symbol is clicked on */
  readonly clickedDiphthong?: (ipa: IpaType) => void;
  /** called when the mouse is over a symbol */
  readonly hovered?: (ipa: IpaType) => void;
  /** map of IPA to [f1,f2] formant frequencies (default: speech synth formants) */
  readonly formants?: Record<IpaType, number[]>;
  /** map diphthongs to the formants of its pieces rather than to 'diphthongFormants' */
  readonly ignoreDiphthongFormants?: boolean;
}

/** display vowels on a formants chart using their two primary frequences */
export function FormantsChart(props: FormantsProps) {
  const formants = props.formants ?? allFormants;
  const show = Object.getOwnPropertyNames(formants);
  const scale = new Scale(props.width, props.height);
  const diphthongs = !props.diphthongs ? [] // no diphthongs
    : Array.isArray(props.diphthongs) ? props.diphthongs  // use passed in list
      : Object.getOwnPropertyNames(props.diphthongs);   // pull out names from lookup table
  const formantsDiphthongs = props.ignoreDiphthongFormants ? {} // no diphthongs
    : Array.isArray(props.diphthongs) ? { ...diphthongFormants, ...rhoticFormants, ...nonsyllabicFormants, ...syllabicFormants } // default list
      : props.diphthongs; // passed in formant table

  // mouse events for symbols
  const cn = props.clicked || props.rightClicked ? 'pointer' : 'nopointer';
  function click(ipa: IpaType) {
    if (props.clicked) {
      props.clicked(ipa);
    }
  }
  function rightClick(e: RightClickEvent, ipa: IpaType) {
    if (props.rightClicked) {
      props.rightClicked(ipa);
      e.preventDefault(); // stop the context menu from appearing
    }
  }
  function over(ipa: IpaType) {
    if (props.hovered) {
      props.hovered(ipa);
    }
  }

  // mouse events for diphthongs
  const cnD = props.clickedDiphthong ? 'pointer' : 'nopointer';
  function clickD(ipa: IpaType) {
    if (props.clickedDiphthong) {
      props.clickedDiphthong(ipa);
    }
  }

  return (<svg width={props.width} height={props.height}>
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" />
      </marker>
    </defs>
    {show.map(ipa => {
      const f = getFormants(ipa, formants);
      if (f.length === 0) { return undefined; }
      const text = props.lookup ? props.lookup(ipa) : ipa;
      const [x, y] = [scale.scaleX(f[0].f2), scale.scaleY(f[0].f1)];
      const fill = props.whatColor ? props.whatColor(ipa) : undefined;
      return (
        <text x={x} y={y} className={cn} key={ipa} fill={fill}
          onClick={() => click(ipa)} onContextMenu={(e) => rightClick(e, ipa)} onMouseOver={() => over(ipa)}>
          {text}
        </text>);
    })}
    {diphthongs.map((ipa, i) => {
      const f = getFormants(ipa, formants, formantsDiphthongs);
      if (f.length < 2 || (f[0].f1 === f[1].f1 && f[0].f2 === f[1].f2)) {
        return undefined;
      }
      const color = props.whatColor ? props.whatColor(ipa) : 'black';
      const [ax, ay] = [scale.scaleX(f[0].f2), scale.scaleY(f[0].f1)];
      const [bx, by] = [scale.scaleX(f[1].f2), scale.scaleY(f[1].f1)];
      const length = Math.sqrt((ax - bx) * (ax - bx) + (ay - by) * (ay - by));
      const [tx, ty] = length < 50 ? [ax - 10, ay - 5] : // short line, move label to start of line
        [(ax + bx) / 2, (ay + by) / 2]; // put label in the middle of line

      // display the diphthong if clicking it does something
      const text = !props.clickedDiphthong ? undefined
        : <text x={tx} y={ty} className={cnD} fill={color}
          onClick={() => clickD(ipa)}>
          {ipa}
        </text>;

      // one line
      if (f.length === 2) {
        return (<g key={ipa + i.toString()}>
          <line x1={ax} y1={ay} x2={bx} y2={by} stroke={color} markerEnd="url(#arrowhead)" />
          {text}
        </g>);
      }
      // two lines
      const [cx, cy] = [scale.scaleX(f[2].f2), scale.scaleY(f[2].f1)];
      return (<g key={ipa + i.toString()}>
        <line x1={ax} y1={ay} x2={bx} y2={by} stroke={color} />
        <line x1={bx} y1={by} x2={cx} y2={cy} stroke={color} markerEnd="url(#arrowhead)" />
        {text}
      </g>);
    })}
  </svg>);
}

/** scale positions based on range of formants */
class Scale {
  readonly xScale;
  readonly xOffset;
  readonly yScale;
  readonly yOffset;

  constructor(private readonly width: number, private readonly height: number) {
    const inset = 10;
    const xRange = [Math.log(700), Math.log(2400)];
    const yRange = [Math.log(250), Math.log(900)];
    this.xScale = (width - 2 * inset) / (xRange[0] - xRange[1]);
    this.xOffset = inset - xRange[1] * this.xScale;
    this.yScale = (height - 2 * inset) / (yRange[1] - yRange[0]);
    this.yOffset = inset - yRange[0] * this.yScale;
  }

  scaleX(x: number): number {
    return Math.log(x) * this.xScale + this.xOffset;
  }

  scaleY(y: number): number {
    return Math.log(y) * this.yScale + this.yOffset;
  }
}

interface RightClickEvent {
  preventDefault(): void;
}
